import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, Users, FileText, GraduationCap, Award, MessageSquare, BookOpen,
  CheckCircle, Plus, Edit, X, CreditCard
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { useAuth } from "../contexts";
import { apiCall } from "../lib/api";

export const TeacherDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateAnnouncement, setShowCreateAnnouncement] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({ title: "", content: "", category: "General", priority: "normal" });
  const [submitting, setSubmitting] = useState(false);
  const [courseTeachers, setCourseTeachers] = useState({});
  const [editingCourse, setEditingCourse] = useState(null);
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [newCourse, setNewCourse] = useState({ name: "", room: "", schedule: "" });
  const [customCourses, setCustomCourses] = useState([]);

  useEffect(() => {
    if (!user) { navigate("/portal"); return; }
    if (user.user_type !== "teacher" && user.user_type !== "admin") { navigate("/dashboard"); return; }
    fetchData();
    const savedTeachers = localStorage.getItem("courseTeachers");
    if (savedTeachers) setCourseTeachers(JSON.parse(savedTeachers));
    const savedCustomCourses = localStorage.getItem("customCourses");
    if (savedCustomCourses) setCustomCourses(JSON.parse(savedCustomCourses));
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [usersRes, announcementsRes] = await Promise.all([
        apiCall("GET", "/users", null, token),
        apiCall("GET", "/announcements", null, token)
      ]);
      setStudents(usersRes.filter(u => u.user_type === "student"));
      setAnnouncements(announcementsRes);
    } catch (err) { console.error("Failed to fetch data:", err); }
    setLoading(false);
  };

  const handleCreateAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.content) { toast.error("Please fill in all required fields"); return; }
    setSubmitting(true);
    try {
      await apiCall("POST", "/announcements", announcementForm, token);
      toast.success("Announcement created successfully!");
      setShowCreateAnnouncement(false);
      setAnnouncementForm({ title: "", content: "", category: "General", priority: "normal" });
      fetchData();
    } catch (err) { toast.error("Failed to create announcement"); }
    setSubmitting(false);
  };

  const saveCourseTeacher = (courseName, teacherName) => {
    const updated = { ...courseTeachers, [courseName]: teacherName };
    setCourseTeachers(updated);
    localStorage.setItem("courseTeachers", JSON.stringify(updated));
    setEditingCourse(null);
    toast.success("Teacher assigned successfully!");
  };

  const handleAddCourse = () => {
    if (!newCourse.name || !newCourse.room || !newCourse.schedule) { toast.error("Please fill in all course fields"); return; }
    const updatedCourses = [...customCourses, newCourse];
    setCustomCourses(updatedCourses);
    localStorage.setItem("customCourses", JSON.stringify(updatedCourses));
    setNewCourse({ name: "", room: "", schedule: "" });
    setShowAddCourse(false);
    toast.success("Course added successfully!");
  };

  const handleDeleteCourse = (courseName) => {
    const updatedCourses = customCourses.filter(c => c.name !== courseName);
    setCustomCourses(updatedCourses);
    localStorage.setItem("customCourses", JSON.stringify(updatedCourses));
    toast.success("Course removed successfully!");
  };

  const getPriorityBadge = (priority) => {
    const styles = { high: "bg-red-100 text-red-600", normal: "bg-blue-100 text-blue-600", low: "bg-green-100 text-green-600" };
    return styles[priority] || styles.normal;
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner"></div></div>;

  const defaultCourses = [
    { name: "Mathematics", room: "Room 101", schedule: "Mon/Wed/Fri 8:00 AM" },
    { name: "English Language", room: "Room 102", schedule: "Mon/Wed/Fri 9:00 AM" },
    { name: "Integrated Science", room: "Lab 201", schedule: "Tue/Thu 8:00 AM" },
    { name: "Social Studies", room: "Room 103", schedule: "Tue/Thu 10:00 AM" },
    { name: "Religious & Moral Education", room: "Room 104", schedule: "Mon/Wed 11:00 AM" },
    { name: "Ghanaian Language (Twi/Fante/Ga)", room: "Room 105", schedule: "Tue/Thu 9:00 AM" },
    { name: "Information & Communication Technology", room: "ICT Lab", schedule: "Wed/Fri 10:00 AM" },
    { name: "French", room: "Room 106", schedule: "Mon/Thu 2:00 PM" },
    { name: "Basic Design & Technology", room: "Workshop", schedule: "Tue/Fri 1:00 PM" },
    { name: "Creative Arts", room: "Art Studio", schedule: "Wed 2:00 PM" },
    { name: "Physical Education", room: "Sports Field", schedule: "Mon/Fri 3:00 PM" },
    { name: "Pre-Technical Skills", room: "Tech Lab", schedule: "Thu 11:00 AM" },
  ];
  const courses = [...defaultCourses, ...customCourses];

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="teacher-dashboard">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8 rounded-2xl overflow-hidden shadow-lg">
          <div className="grid grid-cols-3 h-48">
            <img src="https://images.unsplash.com/photo-1744809482817-9a9d4fc280af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxBZnJpY2FuJTIwc2Nob29sJTIwdGVhY2hlciUyMGNsYXNzcm9vbSUyMGVkdWNhdGlvbnxlbnwwfHx8fDE3Njk5NDI1ODR8MA&ixlib=rb-4.1.0&q=85" alt="Teacher" className="w-full h-full object-cover" />
            <img src="https://images.unsplash.com/photo-1698827623362-f10eadf55a18?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxBZnJpY2FuJTIwc2Nob29sJTIwdGVhY2hlciUyMGNsYXNzcm9vbSUyMGVkdWNhdGlvbnxlbnwwfHx8fDE3Njk5NDI1ODR8MA&ixlib=rb-4.1.0&q=85" alt="Classroom" className="w-full h-full object-cover" />
            <img src="https://images.unsplash.com/flagged/photo-1579133311477-9121405c78dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxBZnJpY2FuJTIwc2Nob29sJTIwdGVhY2hlciUyMGNsYXNzcm9vbSUyMGVkdWNhdGlvbnxlbnwwfHx8fDE3Njk5NDI1ODR8MA&ixlib=rb-4.1.0&q=85" alt="Students" className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-[#002147] rounded-full flex items-center justify-center"><BookOpen className="text-white" size={24} /></div>
            <div>
              <h1 className="text-3xl font-bold text-[#002147]">Teacher Dashboard</h1>
              <p className="text-[#002147]/70">Welcome, {user?.first_name} - Young Christian Preparatory School/ JHS</p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white card-hover"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-500">My Students</p><p className="text-3xl font-bold text-[#002147]">{students.length}</p></div><div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center"><Users className="text-blue-600" size={24} /></div></div></CardContent></Card>
          <Card className="bg-white card-hover"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-500">Announcements</p><p className="text-3xl font-bold text-[#002147]">{announcements.length}</p></div><div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center"><Bell className="text-orange-600" size={24} /></div></div></CardContent></Card>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <Button onClick={() => navigate("/teacher-assignments")} className="bg-[#FF6B00] hover:bg-[#E65C00] h-auto py-4" data-testid="teacher-assignments-btn"><FileText className="mr-2" size={20} /> Assignments</Button>
          <Button onClick={() => navigate("/teacher-attendance")} className="bg-[#002147] hover:bg-[#003366] h-auto py-4" data-testid="teacher-attendance-btn"><CheckCircle className="mr-2" size={20} /> Attendance</Button>
          <Button onClick={() => navigate("/teacher-grades")} className="bg-[#002147] hover:bg-[#003366] h-auto py-4" data-testid="teacher-grades-btn"><Award className="mr-2" size={20} /> Grades</Button>
          <Button onClick={() => navigate("/report-cards")} className="bg-[#FF6B00] hover:bg-[#E65C00] h-auto py-4" data-testid="teacher-report-cards-btn"><GraduationCap className="mr-2" size={20} /> Report Cards</Button>
          <Button onClick={() => navigate("/messages")} className="bg-[#002147] hover:bg-[#003366] h-auto py-4" data-testid="teacher-messages-btn"><MessageSquare className="mr-2" size={20} /> Messages</Button>
          <Button onClick={() => navigate("/my-id-card")} className="bg-[#FF6B00] hover:bg-[#E65C00] h-auto py-4" data-testid="teacher-id-card-btn"><CreditCard className="mr-2" size={20} /> My ID Card</Button>
        </div>

        <Card className="bg-white mb-8 border-2 border-[#002147]">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2 text-xl"><Bell size={24} /> Announcement Board</CardTitle>
              <Button onClick={() => setShowCreateAnnouncement(true)} className="bg-[#FF6B00] hover:bg-[#E65C00]"><Plus size={16} className="mr-2" /> Create New</Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <ScrollArea className="h-[250px]">
              <div className="space-y-4">
                {announcements.map((announcement) => (
                  <Card key={announcement.id} className="border-l-4 border-l-[#FF6B00] bg-gradient-to-r from-yellow-50 to-orange-50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getPriorityBadge(announcement.priority)}>{announcement.priority}</Badge>
                        <Badge variant="outline">{announcement.category}</Badge>
                      </div>
                      <h3 className="text-lg font-semibold text-[#002147]">{announcement.title}</h3>
                      <p className="text-gray-600 mt-1">{announcement.content}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        <Card className="bg-white mb-8">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2"><BookOpen size={20} /> School Courses</CardTitle>
              <Button onClick={() => setShowAddCourse(true)} className="bg-[#FF6B00] hover:bg-[#E65C00]" data-testid="add-course-btn"><Plus size={16} className="mr-2" /> Add Course</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {courses.map((course, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg border-l-4 border-[#FF6B00] relative group">
                  {customCourses.some(c => c.name === course.name) && (
                    <button onClick={() => handleDeleteCourse(course.name)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700" title="Remove course"><X size={16} /></button>
                  )}
                  <h4 className="font-semibold text-[#002147]">{course.name}</h4>
                  <p className="text-xs text-gray-500">{course.schedule}</p>
                  <Badge variant="outline" className="mt-1">{course.room}</Badge>
                  <div className="mt-2 pt-2 border-t">
                    {editingCourse === course.name ? (
                      <div className="flex gap-2">
                        <Input placeholder="Enter teacher name" defaultValue={courseTeachers[course.name]} className="h-8 text-sm" onKeyDown={(e) => { if (e.key === 'Enter') saveCourseTeacher(course.name, e.target.value); }} autoFocus />
                        <Button size="sm" className="h-8 bg-green-600 hover:bg-green-700" onClick={(e) => { const input = e.target.parentElement.querySelector('input'); saveCourseTeacher(course.name, input.value); }}><CheckCircle size={14} /></Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{courseTeachers[course.name] || <span className="italic text-gray-400">No teacher assigned</span>}</span>
                        <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setEditingCourse(course.name)}><Edit size={12} /></Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={showAddCourse} onOpenChange={setShowAddCourse}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><BookOpen size={20} /> Add New Course</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Course Name *</Label><Input value={newCourse.name} onChange={(e) => setNewCourse({...newCourse, name: e.target.value})} placeholder="e.g., Home Economics" /></div>
            <div><Label>Room/Location *</Label><Input value={newCourse.room} onChange={(e) => setNewCourse({...newCourse, room: e.target.value})} placeholder="e.g., Room 201" /></div>
            <div><Label>Schedule *</Label><Input value={newCourse.schedule} onChange={(e) => setNewCourse({...newCourse, schedule: e.target.value})} placeholder="e.g., Mon/Wed 10:00 AM" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAddCourse(false)}>Cancel</Button><Button className="bg-[#FF6B00] hover:bg-[#E65C00]" onClick={handleAddCourse}>Add Course</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCreateAnnouncement} onOpenChange={setShowCreateAnnouncement}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Bell size={20} /> Create New Announcement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title *</Label><Input value={announcementForm.title} onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})} placeholder="Enter announcement title" /></div>
            <div><Label>Content *</Label><Textarea rows={4} value={announcementForm.content} onChange={(e) => setAnnouncementForm({...announcementForm, content: e.target.value})} placeholder="Enter announcement content..." /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Category</Label><Select value={announcementForm.category} onValueChange={(v) => setAnnouncementForm({...announcementForm, category: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="General">General</SelectItem><SelectItem value="Academic">Academic</SelectItem><SelectItem value="Sports">Sports</SelectItem><SelectItem value="Events">Events</SelectItem></SelectContent></Select></div>
              <div><Label>Priority</Label><Select value={announcementForm.priority} onValueChange={(v) => setAnnouncementForm({...announcementForm, priority: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="high">High</SelectItem></SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreateAnnouncement(false)}>Cancel</Button><Button className="bg-[#FF6B00] hover:bg-[#E65C00]" onClick={handleCreateAnnouncement} disabled={submitting}>{submitting ? "Creating..." : "Create Announcement"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
